import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Button, Flex, HStack, IconButton, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spinner, Stack, Text,
  VStack, useToast,
} from '@chakra-ui/react';
import { encodeMp3, samplePeaks } from './audioClip';

const MAX_SECONDS = 60;
// Mureka rejects reference audio shorter than 30s ("The duration of
// instrumentals extracted from the audio file should be at least 30
// seconds", verified 2026-08-17). Block the stop button until we've
// captured enough — even a couple seconds of headroom above the
// threshold is worth it since Mureka's own silence-stripping can trim
// a few seconds off what we ship.
const MIN_SECONDS = 32;
const LEVEL_BARS = 24;

/**
 * Shazam-style mic recorder that captures a short clip and hands back
 * an MP3 File shaped exactly like the clip picker's output — same
 * downstream path (localReference → Generator submit → Mureka).
 *
 * Flow:
 *   1. User taps the mic button → getUserMedia({ audio: true })
 *   2. MediaRecorder collects chunks; AnalyserNode drives a live bar meter
 *   3. Auto-stops at MAX_SECONDS (or user taps stop)
 *   4. On stop: decode the recorded blob → encodeMp3 → onConfirm(File)
 *
 * Permissions: getUserMedia throws on iOS Safari + Android Chrome if
 * the user denies or the site isn't HTTPS. We surface both as friendly
 * toasts and close cleanly rather than leaving the modal stuck.
 *
 * Format: MediaRecorder's default codec varies (Chrome: webm/opus,
 * Safari: mp4/aac). We always re-encode via lamejs so the reference
 * upload matches what we know Mureka accepts (128 kbps mono MP3).
 */
export interface MicRecorderModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (recordedFile: File) => void;
}

type Phase = 'idle' | 'requesting' | 'recording' | 'review' | 'encoding' | 'error';

export function MicRecorderModal({ isOpen, onCancel, onConfirm }: MicRecorderModalProps) {
  const toast = useToast();
  const [phase, setPhase] = useState<Phase>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [levels, setLevels] = useState<number[]>(() => new Array(LEVEL_BARS).fill(0));
  const [error, setError] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);
  const startedAtRef = useRef<number>(0);
  const autoStopRef = useRef<number | null>(null);

  // Review-phase state — populated in handleRecordingStopped, consumed by
  // the review UI (waveform + playback + confirm/re-record buttons).
  const [reviewBuffer, setReviewBuffer] = useState<AudioBuffer | null>(null);
  const [reviewPeaks, setReviewPeaks] = useState<Float32Array | null>(null);
  const [reviewPlaying, setReviewPlaying] = useState(false);
  const [reviewPlayhead, setReviewPlayhead] = useState(0);
  const reviewNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const reviewCtxRef = useRef<AudioContext | null>(null);
  const reviewRafRef = useRef<number | null>(null);
  const reviewStartCtxTimeRef = useRef<number>(0);

  // Full teardown — safe to call at any point in the lifecycle. Stops
  // the mic tracks (removes the browser's red "recording" indicator),
  // tears down the audio graph, cancels timers. Called on close, on
  // cancel, and after successful encode.
  const teardown = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch { /* already stopped */ }
    }
    recorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
    if (autoStopRef.current) { window.clearTimeout(autoStopRef.current); autoStopRef.current = null; }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* noop */ }
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    // Review playback teardown — stop the buffer source, cancel its rAF,
    // close its context. Kept in the same helper so a full close always
    // leaves zero audio/rAF/mic activity behind.
    stopReviewPlayback();
    if (reviewCtxRef.current) {
      try { reviewCtxRef.current.close(); } catch { /* noop */ }
      reviewCtxRef.current = null;
    }
  }, []);

  const stopReviewPlayback = () => {
    if (reviewNodeRef.current) {
      try { reviewNodeRef.current.stop(); } catch { /* already stopped */ }
      reviewNodeRef.current = null;
    }
    if (reviewRafRef.current) { cancelAnimationFrame(reviewRafRef.current); reviewRafRef.current = null; }
    setReviewPlaying(false);
    setReviewPlayhead(0);
  };

  // Reset state whenever the modal opens; teardown on unmount.
  useEffect(() => {
    if (!isOpen) {
      teardown();
      setPhase('idle');
      setElapsedMs(0);
      setLevels(new Array(LEVEL_BARS).fill(0));
      setError(null);
      setReviewBuffer(null);
      setReviewPeaks(null);
    }
    return () => teardown();
  }, [isOpen, teardown]);

  const startRecording = async () => {
    setError(null);
    setPhase('requesting');
    try {
      // Ask for mono if we can — halves the bytes and mono is what we
      // send to Mureka anyway. Browsers may ignore the constraint but
      // never fail on it.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;

      // Level meter graph — separate from the recorder, both consume the
      // same MediaStreamTrack independently.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const AC = window.AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = new AC();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.75;
      src.connect(analyser);
      analyserRef.current = analyser;

      // Recorder — let the browser pick a supported codec. We re-encode
      // on stop, so we don't care whether we get webm/opus or mp4/aac.
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        void handleRecordingStopped();
      };
      recorder.start();
      recorderRef.current = recorder;

      startedAtRef.current = performance.now();
      setPhase('recording');
      setElapsedMs(0);

      // Timer tick — updates the counter + reads the analyser for bars.
      const bins = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(bins);
        // Bucket the low-mid band across LEVEL_BARS bars for a visualiser
        // that reacts more to voice/instruments than the very low end.
        const start = 2;
        const end = Math.min(bins.length, 96);
        const perBar = Math.max(1, Math.floor((end - start) / LEVEL_BARS));
        const next: number[] = new Array(LEVEL_BARS);
        for (let i = 0; i < LEVEL_BARS; i++) {
          let peak = 0;
          const s = start + i * perBar;
          const e = Math.min(end, s + perBar);
          for (let j = s; j < e; j++) if (bins[j] > peak) peak = bins[j];
          next[i] = peak / 255;
        }
        setLevels(next);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);

      timerRef.current = window.setInterval(() => {
        setElapsedMs(performance.now() - startedAtRef.current);
      }, 100);

      // Auto-stop at MAX_SECONDS so we never accidentally record forever.
      autoStopRef.current = window.setTimeout(() => stopRecording(), MAX_SECONDS * 1000);
    } catch (err: any) {
      setPhase('error');
      // eslint-disable-next-line no-console
      console.error('[MixGenie] mic access failed:', err);
      const isDenied = err?.name === 'NotAllowedError' || /denied|permission/i.test(err?.message || '');
      const msg = isDenied
        ? 'Microphone permission denied. Enable it in your browser settings and try again.'
        : (err?.message || 'Could not access the microphone on this device');
      setError(msg);
      toast({ title: 'Mic not available', description: msg, status: 'error', duration: 4500 });
      teardown();
    }
  };

  const stopRecording = () => {
    if (autoStopRef.current) { window.clearTimeout(autoStopRef.current); autoStopRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch { /* noop */ }
    }
    // onstop handler kicks off handleRecordingStopped()
  };

  const handleRecordingStopped = async () => {
    // Move to the review phase — the user gets to LISTEN before we
    // encode + ship to the generator. Decoding is fast (raw blob →
    // AudioBuffer via decodeAudioData); encoding runs on confirm.
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
    // Stop the mic tracks but keep the review-side audio context around
    // so playback works without asking permission again.
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    try {
      const rawBlob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || 'audio/webm' });
      chunksRef.current = [];
      // Fresh context for decode + playback — the recording context that
      // fed the level meter is closed on teardown of the recording phase.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const AC = window.AudioContext || (window as any).webkitAudioContext;
      const ctx = new AC();
      reviewCtxRef.current = ctx;
      const buf = await ctx.decodeAudioData(await rawBlob.arrayBuffer());

      // Downmix to mono up-front — the file we ship must be mono, and
      // it means playback preview matches exactly what generation gets.
      const mono = buf.numberOfChannels === 1 ? buf : downmixToMono(buf, ctx);

      setReviewBuffer(mono);
      setReviewPeaks(samplePeaks(mono, 200));
      setPhase('review');
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[MixGenie] recording decode failed:', err);
      setPhase('error');
      setError(err?.message || 'Could not read the recording');
      toast({
        title: 'Recording could not be processed',
        description: err?.message || 'Try again — a longer clip usually helps',
        status: 'error',
        duration: 4500,
      });
      teardown();
    }
  };

  const playReview = () => {
    if (!reviewBuffer || !reviewCtxRef.current) return;
    stopReviewPlayback();
    const ctx = reviewCtxRef.current;
    if (ctx.state === 'suspended') { ctx.resume().catch(() => undefined); }
    const node = ctx.createBufferSource();
    node.buffer = reviewBuffer;
    node.connect(ctx.destination);
    node.onended = () => {
      setReviewPlaying(false);
      setReviewPlayhead(0);
      if (reviewRafRef.current) { cancelAnimationFrame(reviewRafRef.current); reviewRafRef.current = null; }
    };
    node.start(0);
    reviewNodeRef.current = node;
    reviewStartCtxTimeRef.current = ctx.currentTime;
    setReviewPlaying(true);
    // Playhead follows AudioContext.currentTime for a smooth line.
    const tick = () => {
      const n = reviewNodeRef.current;
      if (!n) return;
      const elapsed = ctx.currentTime - reviewStartCtxTimeRef.current;
      setReviewPlayhead(Math.min(reviewBuffer.duration, elapsed));
      reviewRafRef.current = requestAnimationFrame(tick);
    };
    reviewRafRef.current = requestAnimationFrame(tick);
  };

  const reRecord = () => {
    stopReviewPlayback();
    setReviewBuffer(null);
    setReviewPeaks(null);
    setElapsedMs(0);
    setPhase('idle');
    // User will tap the mic again — same as first-time start.
  };

  const confirmReview = async () => {
    if (!reviewBuffer) return;
    stopReviewPlayback();
    setPhase('encoding');
    // Yield a paint so "Preparing…" actually renders before the encoder
    // starts hogging the main thread (~500ms for 60s).
    await new Promise((r) => setTimeout(r, 0));
    try {
      const mp3 = encodeMp3(reviewBuffer);
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const file = new File([mp3], `recording-${stamp}-clip.mp3`, { type: 'audio/mpeg' });
      teardown();
      onConfirm(file);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[MixGenie] recording encode failed:', err);
      setPhase('review');
      toast({
        title: 'Recording could not be encoded',
        description: err?.message || 'Try recording again',
        status: 'error',
        duration: 4500,
      });
    }
  };

  const handleClose = () => {
    if (phase === 'encoding') return; // don't let the user bail mid-encode
    teardown();
    onCancel();
  };

  const seconds = Math.floor(elapsedMs / 1000);
  const remaining = Math.max(0, MAX_SECONDS - seconds);
  const canStop = seconds >= MIN_SECONDS;
  const untilMin = Math.max(0, MIN_SECONDS - seconds);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      size={{ base: 'full', md: 'md' }}
      scrollBehavior="inside"
      closeOnEsc={phase !== 'encoding'}
      closeOnOverlayClick={phase !== 'encoding'}
    >
      <ModalOverlay />
      <ModalContent bg="night.800" color="whiteAlpha.900">
        <ModalHeader>
          <Stack spacing={0}>
            <Text fontSize="xs" color="whiteAlpha.600" textTransform="uppercase" letterSpacing="0.08em">
              Reference audio
            </Text>
            <Text fontSize="md" noOfLines={1}>
              {phase === 'encoding'
                ? 'Preparing…'
                : phase === 'review'
                  ? 'Review your recording'
                  : 'Record from microphone'}
            </Text>
          </Stack>
        </ModalHeader>
        {phase !== 'encoding' && <ModalCloseButton />}
        <ModalBody>
          {phase === 'encoding' ? (
            <VStack spacing={5} py={{ base: 12, md: 16 }}>
              <Spinner size="xl" color="pink.300" thickness="3px" />
              <Stack spacing={1} textAlign="center">
                <Text color="whiteAlpha.900" fontWeight={700} fontSize="lg">
                  Preparing your recording…
                </Text>
                <Text color="whiteAlpha.600" fontSize="sm">
                  Encoding audio and opening the generator
                </Text>
              </Stack>
            </VStack>
          ) : phase === 'review' && reviewBuffer && reviewPeaks ? (
            <VStack spacing={5} py={4}>
              {/* Waveform of the just-recorded clip with a live playhead
                  while previewing — same visual grammar as the clip picker
                  so the two flows read as one feature. */}
              <ReviewWaveform peaks={reviewPeaks} playheadPct={reviewBuffer.duration > 0 ? (reviewPlayhead / reviewBuffer.duration) * 100 : 0} />
              <IconButton
                aria-label={reviewPlaying ? 'Pause preview' : 'Play preview'}
                onClick={reviewPlaying ? stopReviewPlayback : playReview}
                boxSize="72px"
                borderRadius="full"
                bgGradient="linear(135deg, pink.400, purple.500)"
                color="white"
                _hover={{ opacity: 0.92 }}
                _active={{ transform: 'scale(0.97)' }}
                icon={
                  reviewPlaying ? (
                    <svg viewBox="0 0 24 24" width="30" height="30" fill="currentColor">
                      <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="30" height="30" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )
                }
              />
              <Stack spacing={1} textAlign="center">
                <Text fontSize="xl" fontWeight={800} color="whiteAlpha.900" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatMMSS(reviewPlaying ? Math.floor(reviewPlayhead) : Math.floor(reviewBuffer.duration))}
                  <Text as="span" color="whiteAlpha.500" fontWeight={500}>
                    {reviewPlaying ? ` / ${formatMMSS(Math.floor(reviewBuffer.duration))}` : ''}
                  </Text>
                </Text>
                <Text fontSize="xs" color="whiteAlpha.600">
                  Listen back before you send it to the generator
                </Text>
              </Stack>
            </VStack>
          ) : (
            <VStack spacing={6} py={4}>
              <LevelMeter levels={levels} active={phase === 'recording'} />
              <MicButton
                phase={phase}
                canStop={canStop}
                onStart={startRecording}
                onStop={stopRecording}
              />
              <Stack spacing={1} textAlign="center">
                <Text fontSize="3xl" fontWeight={800} color="whiteAlpha.900" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatMMSS(seconds)}
                </Text>
                <Text fontSize="xs" color={phase === 'recording' && !canStop ? 'yellow.300' : 'whiteAlpha.600'}>
                  {phase === 'recording'
                    ? canStop
                      ? `${remaining}s remaining · tap to stop`
                      : `Keep going — ${untilMin}s until you can stop (Mureka needs ≥ ${MIN_SECONDS - 2}s)`
                    : phase === 'requesting'
                      ? 'Waiting for microphone permission…'
                      : phase === 'error'
                        ? error || 'Something went wrong'
                        : `Tap to start · min ${MIN_SECONDS - 2}s, up to ${MAX_SECONDS}s`}
                </Text>
              </Stack>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          {phase === 'review' ? (
            <HStack w="full" justify="space-between">
              <Button variant="ghost" colorScheme="whiteAlpha" onClick={reRecord} size="sm">
                ↺ Re-record
              </Button>
              <Button colorScheme="pink" onClick={confirmReview}>
                Use this recording
              </Button>
            </HStack>
          ) : phase !== 'encoding' && (
            <HStack w="full" justify="flex-end">
              <Button variant="ghost" colorScheme="whiteAlpha" onClick={handleClose}>
                Cancel
              </Button>
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/** Big central mic button — pulses gently while recording. Stop is
    disabled until we've captured the Mureka minimum, so the user can't
    accidentally submit a too-short clip that will fail server-side. */
function MicButton({
  phase, canStop, onStart, onStop,
}: {
  phase: Phase; canStop: boolean; onStart: () => void; onStop: () => void;
}) {
  const recording = phase === 'recording';
  const busy = phase === 'requesting';
  // While recording but before the minimum, we still show the record-red
  // colour (recording is happening), but the button acts as a no-op tap —
  // Chakra's isDisabled would grey it out which reads as "broken", so we
  // keep the pulse animation and just swallow the click.
  const stopBlocked = recording && !canStop;
  const handleClick = () => {
    if (recording) { if (canStop) onStop(); return; }
    onStart();
  };
  return (
    <>
      <style>{`
        @keyframes mg-mic-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(233, 74, 143, 0.55); }
          50%      { box-shadow: 0 0 0 18px rgba(233, 74, 143, 0); }
        }
      `}</style>
      <IconButton
        aria-label={recording ? (canStop ? 'Stop recording' : 'Recording — minimum length not reached') : 'Start recording'}
        onClick={handleClick}
        isDisabled={busy}
        boxSize="96px"
        borderRadius="full"
        bgGradient={recording ? 'linear(135deg, red.400, pink.500)' : 'linear(135deg, pink.400, purple.500)'}
        color="white"
        opacity={stopBlocked ? 0.75 : 1}
        cursor={stopBlocked ? 'not-allowed' : 'pointer'}
        _hover={{ opacity: stopBlocked ? 0.75 : 0.92 }}
        _active={{ transform: stopBlocked ? 'none' : 'scale(0.97)' }}
        style={recording ? { animation: 'mg-mic-pulse 1.4s ease-in-out infinite' } : undefined}
        icon={
          recording ? (
            <Box w="26px" h="26px" bg="white" borderRadius="4px" />
          ) : busy ? (
            <Spinner color="white" thickness="3px" size="lg" />
          ) : (
            <svg viewBox="0 0 24 24" width="42" height="42" fill="currentColor">
              <path d="M12 14a3 3 0 003-3V5a3 3 0 00-6 0v6a3 3 0 003 3z" />
              <path d="M19 11a1 1 0 10-2 0 5 5 0 01-10 0 1 1 0 10-2 0 7 7 0 006 6.92V21h2v-3.08A7 7 0 0019 11z" />
            </svg>
          )
        }
      />
    </>
  );
}

/** Bar level meter — one column per LEVEL_BARS, driven by AnalyserNode data. */
function LevelMeter({ levels, active }: { levels: number[]; active: boolean }) {
  return (
    <Flex
      h="60px"
      w="full"
      align="center"
      justify="center"
      gap="3px"
      opacity={active ? 1 : 0.35}
    >
      {levels.map((v, i) => (
        <Box
          key={i}
          w="4px"
          h={`${Math.max(6, v * 100)}%`}
          bg="pink.300"
          borderRadius="2px"
          transition="height 60ms linear"
        />
      ))}
    </Flex>
  );
}

/** Waveform strip for the review phase — bars + a moving playhead line
    driven by AudioContext.currentTime while playback is running. */
function ReviewWaveform({ peaks, playheadPct }: { peaks: Float32Array; playheadPct: number }) {
  const bars = Array.from(peaks);
  return (
    <Box position="relative" h="80px" w="full" bg="rgba(10, 8, 20, 0.55)" borderRadius="8px" overflow="hidden">
      {playheadPct > 0 && (
        <Box
          position="absolute"
          top={0}
          bottom={0}
          left={`${playheadPct}%`}
          w="2px"
          bg="pink.200"
          boxShadow="0 0 6px rgba(233, 74, 143, 0.9)"
          pointerEvents="none"
          zIndex={2}
        />
      )}
      <Flex position="absolute" inset={0} align="center" px={1} gap="1px">
        {bars.map((p, i) => (
          <Box
            key={i}
            flex="1"
            h={`${Math.max(2, p * 100)}%`}
            bg="whiteAlpha.600"
            borderRadius="1px"
            minW="1px"
          />
        ))}
      </Flex>
    </Box>
  );
}

function downmixToMono(buf: AudioBuffer, ctx: AudioContext): AudioBuffer {
  const out = ctx.createBuffer(1, buf.length, buf.sampleRate);
  const dst = out.getChannelData(0);
  const chCount = buf.numberOfChannels;
  for (let ch = 0; ch < chCount; ch++) {
    const src = buf.getChannelData(ch);
    for (let i = 0; i < src.length; i++) dst[i] += src[i] / chCount;
  }
  return out;
}

function formatMMSS(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec < 10 ? '0' : ''}${sec}`;
}
